import React, { useState, useImperativeHandle, useRef, forwardRef, useCallback } from 'react';
import PropTypes from 'prop-types';

const LoadFromBackendMenuItemHandler = forwardRef((props, ref) => {
  const { getComponent, backendSpecsActions, backendSpecsSelectors } = props;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  const alertDialogMessage = useRef('');

  const LoadFromBackendDialog = getComponent('LoadFromBackendDialog', true);
  const AlertDialog = getComponent('AlertDialog', true);

  const handleDialogClose = useCallback((result) => {
    setIsDialogOpen(false);
  }, []);

  const handleSpecSelect = useCallback(
    async (specId) => {
      if (!backendSpecsActions?.loadSpecIntoEditor) {
        alertDialogMessage.current = 'Backend specs plugin not properly configured';
        setIsDialogOpen(false);
        setIsAlertDialogOpen(true);
        return;
      }

      const result = await backendSpecsActions.loadSpecIntoEditor(specId);

      if (result.error) {
        alertDialogMessage.current = result.meta?.errorMessage || 'Failed to load spec';
        setIsDialogOpen(false);
        setIsAlertDialogOpen(true);
      } else {
        setIsDialogOpen(false);
      }
    },
    [backendSpecsActions]
  );

  const handleAlertDialogClose = useCallback(() => {
    setIsAlertDialogOpen(false);
  }, []);

  useImperativeHandle(ref, () => ({
    openDialog() {
      setIsDialogOpen(true);
    },
  }));

  return (
    <>
      {AlertDialog && (
        <AlertDialog
          isOpen={isAlertDialogOpen}
          title="Error Loading Spec"
          onClose={handleAlertDialogClose}
        >
          {alertDialogMessage.current}
        </AlertDialog>
      )}
      {LoadFromBackendDialog && (
        <LoadFromBackendDialog
          isOpen={isDialogOpen}
          onClose={handleDialogClose}
          onSelect={handleSpecSelect}
          getComponent={getComponent}
          backendSpecsActions={backendSpecsActions}
          backendSpecsSelectors={backendSpecsSelectors}
        />
      )}
    </>
  );
});

LoadFromBackendMenuItemHandler.propTypes = {
  getComponent: PropTypes.func.isRequired,
  backendSpecsActions: PropTypes.shape({
    loadSpecIntoEditor: PropTypes.func,
  }),
  backendSpecsSelectors: PropTypes.shape({
    selectSpecsList: PropTypes.func,
    selectSpecsListLoading: PropTypes.func,
    selectSpecsListError: PropTypes.func,
  }),
};

LoadFromBackendMenuItemHandler.defaultProps = {
  backendSpecsActions: null,
  backendSpecsSelectors: null,
};

export default LoadFromBackendMenuItemHandler;
